/**
 * P1.4.1 Performance Dashboard Component
 * 
 * Observability panel showing:
 * - Cache KPIs (hit_rate, entries, evictions)
 * - API Latency KPIs (p50/p95 per endpoint group)
 * - Gate/Workflow KPIs (failure counts)
 * 
 * Read-only with manual refresh (no heavy polling).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Activity, 
  Database, 
  Clock, 
  AlertTriangle,
  RefreshCw,
  Gauge,
  Zap,
  Shield,
  Server,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  BarChart3,
  Timer,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
const API = ''; // Relative URL - browser uses same origin

const PerformanceDashboard = ({ isActive = false }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Metrics data
  const [cacheStats, setCacheStats] = useState(null);
  const [latencyStats, setLatencyStats] = useState(null);
  const [gateStats, setGateStats] = useState(null);
  const [uptime, setUptime] = useState(0);
  
  // Auto-refresh toggle
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Fetch all metrics
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/metrics/all`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setCacheStats(data.cache);
        setLatencyStats(data.latency);
        setGateStats(data.gates);
        setUptime(data.uptime_seconds);
        setLastUpdated(new Date());
      } else {
        toast.error('Fehler beim Laden der Metriken');
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);
  
  // Clear cache handler
  const clearCache = async (namespace = null) => {
    try {
      const url = namespace 
        ? `${API}/api/admin/cache/clear?namespace=${namespace}`
        : `${API}/api/admin/cache/clear`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success(namespace ? `Cache ${namespace} geleert` : 'Gesamter Cache geleert');
        await fetchMetrics();
      } else {
        toast.error('Cache-Clearing fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };
  
  // Lazy load when active
  useEffect(() => {
    if (isActive && !initialLoadDone) {
      fetchMetrics();
      setInitialLoadDone(true);
    }
  }, [isActive, initialLoadDone, fetchMetrics]);
  
  // Auto-refresh (optional, every 10s)
  useEffect(() => {
    let interval;
    if (autoRefresh && isActive) {
      interval = setInterval(fetchMetrics, 10000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, isActive, fetchMetrics]);
  
  // Format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  // Get status color for hit rate
  const getHitRateColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Get status color for latency (p95)
  const getLatencyColor = (p95) => {
    if (p95 < 100) return 'text-green-600';
    if (p95 < 500) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  return (
    <div className="space-y-6" data-testid="performance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gauge className="w-6 h-6 text-blue-600" />
            Performance Dashboard
          </h2>
          <p className="text-gray-600 mt-1">
            Observability KPIs (P1.4.1)
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Aktualisiert: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto (10s)
          </label>
          <Button
            variant="outline"
            onClick={fetchMetrics}
            disabled={loading}
            data-testid="refresh-metrics"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* Uptime Banner */}
      {uptime > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-600" />
            <span className="font-medium">Server Uptime:</span>
            <span className="text-blue-700">{formatDuration(uptime)}</span>
          </div>
          <Badge variant="outline" className="bg-white">
            Backend Healthy
          </Badge>
        </div>
      )}
      
      {/* Cache KPIs */}
      <Card data-testid="cache-kpis">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Cache KPIs
              </CardTitle>
              <CardDescription>In-Memory Cache Performance</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearCache()}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Cache leeren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {cacheStats ? (
            <div className="space-y-4">
              {/* Hit Rate Progress */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Hit Rate</span>
                  <span className={`text-lg font-bold ${getHitRateColor(cacheStats.hit_rate_percent)}`}>
                    {cacheStats.hit_rate_percent}%
                  </span>
                </div>
                <Progress 
                  value={cacheStats.hit_rate_percent} 
                  className="h-2"
                />
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{cacheStats.hits}</div>
                  <div className="text-xs text-gray-600">Hits</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{cacheStats.misses}</div>
                  <div className="text-xs text-gray-600">Misses</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{cacheStats.current_entries}</div>
                  <div className="text-xs text-gray-600">Einträge</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{cacheStats.evictions}</div>
                  <div className="text-xs text-gray-600">Evictions</div>
                </div>
              </div>
              
              {/* Namespaces */}
              {cacheStats.namespaces && Object.keys(cacheStats.namespaces).length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-medium mb-2">Cache Namespaces</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(cacheStats.namespaces).map(([ns, count]) => (
                      <Badge key={ns} variant="outline" className="text-xs">
                        {ns}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {loading ? 'Lade...' : 'Keine Daten verfügbar'}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* API Latency KPIs */}
      <Card data-testid="latency-kpis">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-orange-600" />
            API Latency KPIs
          </CardTitle>
          <CardDescription>
            Rolling Window ({latencyStats?.window_minutes || 15} min) - p50/p95 pro Endpoint-Gruppe
          </CardDescription>
        </CardHeader>
        <CardContent>
          {latencyStats?.groups && Object.keys(latencyStats.groups).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(latencyStats.groups).map(([group, stats]) => (
                <div key={group} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium capitalize">{group}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">
                        {stats.request_count} Requests
                      </span>
                      {stats.error_rate_percent > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {stats.error_rate_percent}% Fehler
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 rounded p-2">
                      <div className="text-lg font-bold text-green-700">
                        {stats.p50_ms}ms
                      </div>
                      <div className="text-xs text-gray-600">p50</div>
                    </div>
                    <div className={`rounded p-2 ${stats.p95_ms < 500 ? 'bg-yellow-50' : 'bg-red-50'}`}>
                      <div className={`text-lg font-bold ${getLatencyColor(stats.p95_ms)}`}>
                        {stats.p95_ms}ms
                      </div>
                      <div className="text-xs text-gray-600">p95</div>
                    </div>
                    <div className="bg-blue-50 rounded p-2">
                      <div className="text-lg font-bold text-blue-700">
                        {stats.avg_ms}ms
                      </div>
                      <div className="text-xs text-gray-600">avg</div>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="text-xs text-gray-500 text-right">
                Gesamt: {latencyStats.total_recent_requests} Requests im Fenster
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {loading ? 'Lade...' : 'Noch keine Latenz-Daten erfasst'}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Gate/Workflow KPIs */}
      <Card data-testid="gate-kpis">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            Gate/Workflow KPIs
          </CardTitle>
          <CardDescription>
            Gate-Fehler und Validierungs-Blockaden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {gateStats ? (
            <div className="space-y-4">
              {/* Recent Failures (1h) */}
              <div>
                <div className="text-sm font-medium mb-2">Letzte Stunde</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.entries(gateStats.recent_failures_1h || {}).map(([gate, count]) => {
                    const isZero = count === 0;
                    return (
                      <div 
                        key={gate} 
                        className={`rounded-lg p-3 text-center ${isZero ? 'bg-green-50' : 'bg-red-50'}`}
                      >
                        <div className={`text-2xl font-bold ${isZero ? 'text-green-600' : 'text-red-600'}`}>
                          {count}
                        </div>
                        <div className="text-xs text-gray-600 truncate" title={gate}>
                          {gate.replace(/_/g, ' ')}
                        </div>
                        {isZero ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mx-auto mt-1" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 mx-auto mt-1" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Total Failures */}
              <div>
                <div className="text-sm font-medium mb-2">Gesamt (seit Start)</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(gateStats.total_failures || {}).map(([gate, count]) => (
                    <Badge 
                      key={gate} 
                      variant={count > 0 ? 'destructive' : 'outline'}
                      className="text-xs"
                    >
                      {gate.replace(/_/g, ' ')}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Compliance Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">Orphan Chunks Compliance</span>
                </div>
                <Badge className="bg-green-100 text-green-800">
                  Aktiv
                </Badge>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {loading ? 'Lade...' : 'Keine Gate-Daten verfügbar'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceDashboard;
